import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Section, VerdictBanner, StatsTable, BandTable, AlertList, Ticker } from "../components/UniverseBacktestParts";
import { SpcxCase, EarningsTable } from "../components/UniverseBacktestEarnings";

function Header({ data, onRun, running }) {
  return (
    <>
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-mb-ink mb-6" data-testid="uxbt-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Vetting cockpit
      </Link>
      <div className="uppercase tracking-widest text-[11px] text-mb-violet font-bold">Analyst desk · Backtest</div>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2 text-mb-ink">Every stock over $500M</h1>
      <p className="mt-3 text-neutral-600 max-w-2xl leading-relaxed">
        12-month replay of the Moonshot scanner on today's small + mid list vs. every US common stock above the
        market-cap floor, under identical rules. The live scanner is unchanged.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-neutral-500">
        {data?.window && <span data-testid="uxbt-window">Window {data.window.start} → {data.window.end} · {data.window.trading_days} trading days</span>}
        {data?.as_of && <span>Run {new Date(data.as_of).toLocaleString()}</span>}
        <Button size="sm" variant="outline" onClick={onRun} disabled={running} className="rounded-md" data-testid="uxbt-run-btn">
          {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
          {running ? "Running… (a few minutes)" : "Run again"}
        </Button>
      </div>
    </>
  );
}

function Method({ rules, coverage, priceCap }) {
  return (
    <Section title="Method and coverage" testId="uxbt-method">
      <ul className="space-y-2 text-sm text-neutral-700 list-disc pl-5">
        <li>Rules in both books: confidence ≥ {rules.min_confidence}, market cap ≥ ${(rules.market_cap_min / 1e6).toFixed(0)}M, price ≤ ${rules.price_max}, {rules.cap_per_cycle} alerts per day, {rules.min_gap_days}-day re-alert gap. {rules.exits}.</li>
        <li>{rules.cycle}</li>
        <li>{rules.ranking}</li>
        <li><b>Not replayed:</b> {rules.not_replayed}</li>
        <li>Coverage: {coverage.expanded_with_data} of {coverage.expanded_universe} expanded symbols and {coverage.current_with_data} of {coverage.current_universe} current symbols had price history ({coverage.missing_count} missing).</li>
        <li>{coverage.market_cap_method}</li>
        <li>{coverage.survivorship_note}</li>
        <li>{coverage.earnings_events_scanned.toLocaleString()} earnings reports scanned (Nasdaq calendar). Moves measured close-to-close around the report; when the report time isn't published, the larger of the two candidate sessions is used and marked "inferred".</li>
      </ul>
      <div className="mt-5" data-testid="uxbt-price-cap">
        <div className="text-sm font-semibold text-mb-ink">The ${priceCap.price_max} price cap (information only, not changed)</div>
        <p className="text-sm text-neutral-600 mt-1">
          {priceCap.symbols} big names passed every other rule but were blocked only by the price cap, on {priceCap.candidate_days} candidate-days.
        </p>
        {priceCap.top.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {priceCap.top.map((t) => <span key={t.symbol}><Ticker sym={t.symbol} /> ×{t.days}</span>)}
          </div>
        )}
      </div>
    </Section>
  );
}

export default function AdminUniverseBacktest() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const running = data?.run_status === "running";

  const load = useCallback(() => api.get("/admin/backtests/universe-expansion").then((r) => setData(r.data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [running, load]);

  const run = async () => {
    try {
      await api.post("/admin/backtests/universe-expansion/run");
      toast.success("Backtest started");
      setData((d) => ({ ...(d || {}), run_status: "running" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the run");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-mb-sand flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-mb-emerald" /></div>;
  }

  return (
    <div className="bg-mb-sand min-h-screen" data-testid="uxbt-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        <Header data={data} onRun={run} running={running} />
        {data?.verdict && data?.run_status === "failed" && (
          <p className="mt-6 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-600" data-testid="uxbt-run-failed">
            The last run failed{data.run_error ? `: ${data.run_error}` : ""}. Showing the previous results.
          </p>
        )}
        {!data?.verdict ? (
          <p className="mt-10 text-sm text-neutral-600" data-testid="uxbt-empty">{running ? "The backtest is running — results appear here when it finishes." : data?.run_error ? `Last run failed: ${data.run_error}` : "No results yet. Click Run again."}</p>
        ) : (
          <>
            <VerdictBanner verdict={data.verdict} current={data.current} expanded={data.expanded} />
            <Section title="SPCX case study" sub="Would the expanded scanner have alerted SPCX around its earnings?" testId="uxbt-spcx">
              <SpcxCase ev={data.spcx} note={data.coverage.spcx_note} />
            </Section>
            <Section
              title="Earnings check — would we have caught it?"
              sub={`Stocks over $30B that moved 7%+ on earnings: ${data.earnings_summary.events} reports · ${data.earnings_summary.caught_before} alerted before · ${data.earnings_summary.caught_after} only after · ${data.earnings_summary.missed} not alerted. V11 return is the real alert's result, or a hypothetical entry at the pre-report close.`}
              testId="uxbt-earnings"
            >
              <EarningsTable rows={data.earnings} />
              <Link to="/admin/backtests/earnings-signal" className="mt-4 inline-block text-sm font-semibold text-mb-violet hover:underline" data-testid="uxbt-earnings-signal-link">
                See the earnings-aware entry test for these names →
              </Link>
            </Section>
            <Section title="Side by side" testId="uxbt-compare">
              <StatsTable current={data.current} expanded={data.expanded} testId="uxbt-stats" />
            </Section>
            <Section title="By size band" sub="Market cap at entry." testId="uxbt-bands">
              <BandTable bands={data.bands} />
            </Section>
            <Section title="Added — alerts only the expanded book publishes" sub="* still open, marked to the last close." testId="uxbt-added">
              <AlertList rows={data.added} testId="uxbt-added-list" empty="No added alerts." />
            </Section>
            <Section title="Displaced — current-book alerts that lost their slot" sub="Small/mid alerts that dropped out because a different name took one of the daily slots (or the ripple of an earlier change)." testId="uxbt-displaced">
              <AlertList rows={data.displaced} testId="uxbt-displaced-list" empty="Nothing was displaced." />
            </Section>
            <Method rules={data.rules} coverage={data.coverage} priceCap={data.price_cap} />
          </>
        )}
      </div>
    </div>
  );
}
